import React, { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from "@/components/ui/form";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Save, Upload, Trash, Loader2 } from "lucide-react";
import { useForm, useFieldArray } from "react-hook-form";
import { toast } from "sonner";
import { Separator } from "@/components/ui/separator";
import { ContactForm } from "@/components/merchant/ContactForm";
import * as Yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  Select,
  SelectContent,
  SelectTrigger,
  SelectValue,
  SelectItem,
} from "@/components/ui/select";
import countries from "@/constants/array/country_city.json";
import VirtualizedCitySelect from "@/components/ui/virtualized-city-select";
import { MerchantApi } from "@/api";

/** Offer Point create merchant — UI subset; submit is local-only until new API lands */
const OffersMerchantSchema = Yup.object().shape({
  businessName: Yup.string().required("Merchant Name is required"),
  merchantCode: Yup.string().required("Merchant code is required."),
  tier: Yup.string()
    .oneOf(
      [
        "WESTAY Bronze",
        "WESTAY Silver",
        "WESTAY Gold",
        "WESTAY Platinum",
        "WESTAY Prive",
      ],
      "Select a valid tier"
    )
    .required("Tier is required"),
  businessLocation: Yup.string(),
  businessDescription: Yup.string().required(
    "Merchant description is required"
  ),
  websiteUrl: Yup.string()
    .required("Website URL is required")
    .url("Website URL must be a valid URL format"),
  country: Yup.string().required("Country is required"),
  city: Yup.string().required("City is required"),

  categoryVOList: Yup.array()
    .of(
      Yup.object().shape({
        categoryCode: Yup.string(),
        categoryName: Yup.string().required("Category name is required"),
        cashbackPercentage: Yup.number()
          .typeError("Cashback percentage should be a number")
          .required("Cashback percentage is required")
          .positive("Cashback percentage must be greater than 0")
          .min(0.01, "Cashback percentage must be greater than 0")
          .max(100, "Cashback percentage cannot exceed 100%"),
      })
    )
    .min(1, "At least one category is required"),
  contacts: Yup.array()
    .min(1, "At least one contact is required")
    .of(
      Yup.object().shape({
        contactName: Yup.string()
          .required("Contact name is required")
          .matches(/^[a-zA-Z\s]+$/, "Name can only contain letters and spaces"),
        contactEmail: Yup.string()
          .required("Email is required")
          .email("Email must be a valid email address"),
        contactRole: Yup.string()
          .required("Role is required")
          .matches(/^[a-zA-Z\s]+$/, "Role can only contain letters and spaces"),
        countryCode: Yup.string().required("Country code is required"),
        phoneNumber: Yup.string()
          .required("Phone number is required")
          .matches(/^\d+$/, "Phone number can only contain positive numbers")
          .min(7, "Phone number must be at least 7 digits")
          .max(15, "Phone number cannot exceed 15 digits"),
      })
    ),
});

const OffersCreateMerchant = () => {
  const basePath = "/offers";
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [merchantLogo, setMerchantLogo] = useState();
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showLogoError, setShowLogoError] = useState(false);
  const form = useForm({
    resolver: yupResolver(OffersMerchantSchema),
    mode: "onSubmit",
    defaultValues: {
      businessName: "",
      merchantCode: "",
      tier: "",
      businessDescription: "",
      country: "",
      city: "",
      websiteUrl: "",
      businessLocation: "",
      categoryVOList: [
        { categoryCode: "", categoryName: "", cashbackPercentage: 0 },
      ],
      contacts: [
        {
          contactName: "",
          contactEmail: "",
          countryCode: "+1",
          phoneNumber: "",
          contactRole: "",
        },
      ],
    },
  });

  const {
    fields: categoryFields,
    append: appendCategory,
    remove: removeCategory,
  } = useFieldArray({
    control: form.control,
    name: "categoryVOList",
  });

  const handleBrandLogoChange = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile) {
      const image = new Image();
      image.onload = () => {
        setMerchantLogo(selectedFile);
        setShowLogoError(false); // Clear error when logo is uploaded
      };
      image.src = URL.createObjectURL(selectedFile);
    }
  };

  // Function to check logo before form submission attempt
  const handleSubmitClick = () => {
    if (!merchantLogo) {
      setShowLogoError(true);
      toast.error("Merchant logo is required");
    }
  };

  const handleSubmit = async (formData: any) => {
    // Check merchant logo first (before other validations)
    if (!merchantLogo) {
      setShowLogoError(true);
      toast.error("Merchant logo is required");
      return;
    }

    // Trigger validation for all fields before submission
    const isValid = await form.trigger();
    if (!isValid) {
      toast.error("Please fix validation errors before submitting");
      return;
    }

    setIsLoading(true);
    try {
      const { contacts, ...merchantFormValues } = formData;

      const selectedCountryName =
        selectedCountry?.name ||
        (() => {
          try {
            const parsed = merchantFormValues?.country
              ? JSON.parse(merchantFormValues.country)
              : null;
            return parsed?.name || "";
          } catch {
            return "";
          }
        })();

      const offerMerchantPayload = {
        merchantName: merchantFormValues?.businessName,
        merchantCode: merchantFormValues?.merchantCode,
        tier: merchantFormValues?.tier,
        businessDescription: merchantFormValues?.businessDescription,
        websiteUrl: merchantFormValues?.websiteUrl,
        city: merchantFormValues?.city,
        country: selectedCountryName,
        businessLocation: merchantFormValues?.businessLocation,
        categoryVOList: (merchantFormValues?.categoryVOList || []).map(
          (category) => ({
            cashbackPercentage: Number(category?.cashbackPercentage || 0),
            categoryName: category?.categoryName,
            categoryCode: category?.categoryCode || category?.categoryName,
          })
        ),
      };

      const merchantData = new FormData();
      if (merchantLogo) {
        merchantData.append("merchantLogo", merchantLogo);
      }
      merchantData.append(
        "offeredMerchantVO",
        new Blob([JSON.stringify(offerMerchantPayload)], {
          type: "application/json",
        })
      );

      const createResponse = await MerchantApi.createOrUpdateMerchantOfOfferPage(
        merchantData
      );

      const merchantId =
        createResponse?.data?.id ??
        createResponse?.data?.merchantId ??
        createResponse?.id;

      if (!merchantId) {
        throw new Error("Merchant ID not returned from create API");
      }

      if (contacts?.length) {
        await Promise.all(
          contacts.map((contact) =>
            MerchantApi.createOrUpdateMerchantOfOfferContact({
              merchantId,
              contactName: contact.contactName,
              contactEmail: contact.contactEmail,
              contactRole: contact.contactRole,
              merchantOfOfferContactPhoneVOList: contact.phoneNumber
                ? [
                    {
                      phoneCountryCode: contact.countryCode || "",
                      phoneNumber: contact.phoneNumber,
                    },
                  ]
                : [],
            })
          )
        );
      }

      toast.success("Merchant created successfully");
      navigate(`${basePath}/merchants`);
    } catch (error) {
      console.error("Error creating offer merchant:", error);
      toast.error("Failed to create merchant");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header with back button */}
      <div className="flex items-center gap-4 mb-6">
        <Button variant="outline" size="sm" asChild className="bg-white">
          <Link to={`${basePath}/merchants`}>
            <ArrowLeft className="h-4 w-4 mr-1" /> Back
          </Link>
        </Button>
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-rose-600 mb-1">
            Offer Point
          </p>
          <h1 className="text-2xl font-bold text-gray-800">Create Merchant</h1>
          <p className="text-gray-500">Add a new merchant to your platform</p>
        </div>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-8">
          <Card className="border-0 shadow-sm overflow-hidden bg-white w-full">
            <CardHeader className="card-header-compact bg-gray-50">
              <CardTitle className="text-lg font-medium text-gray-800">
                General Information
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-6">
                <div className="space-y-2">
                  <FormLabel>Merchant Logo*</FormLabel>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    hidden
                    onChange={handleBrandLogoChange}
                  />
                  <div
                    className={`w-24 h-24 bg-gray-100 rounded-lg flex flex-col items-center justify-center border-2 border-dashed cursor-pointer hover:bg-gray-50 transition-all ${
                      showLogoError && !merchantLogo
                        ? "border-red-500"
                        : "border-gray-200"
                    }`}
                    onClick={() => {
                      console.log("CLICKED", fileInputRef?.current);
                      fileInputRef?.current?.click();
                    }}
                  >
                    {merchantLogo ? (
                      <img
                        src={URL.createObjectURL(merchantLogo)}
                        alt={form?.getValues("businessName") || "Business Logo"}
                        className="max-h-16 max-w-16"
                        id="fileInput"
                      />
                    ) : (
                      <>
                        <Upload className="h-6 w-6 text-gray-400" />
                        <span className="text-xs text-gray-500 mt-1">
                          Upload Logo
                        </span>
                      </>
                    )}
                  </div>
                  <p className="text-xs text-gray-500">
                    Recommended: 1200 × 800px (3:2 ratio)
                  </p>
                  {showLogoError && !merchantLogo && (
                    <p className="text-sm text-red-600">
                      Merchant logo is required
                    </p>
                  )}
                </div>

                <FormField
                  control={form.control}
                  name="businessName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Merchant Name*</FormLabel>
                      <FormControl>
                        <Input placeholder="Enter merchant name" {...field} />
                      </FormControl>
                      <FormDescription>
                        The official name of the merchant
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="merchantCode"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Merchant Code*</FormLabel>
                      <FormControl>
                        <Input placeholder="Enter merchant code" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="tier"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Tier*</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        value={field.value}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select tier" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="WESTAY Bronze">WESTAY Bronze</SelectItem>
                          <SelectItem value="WESTAY Silver">WESTAY Silver</SelectItem>
                          <SelectItem value="WESTAY Gold">WESTAY Gold</SelectItem>
                          <SelectItem value="WESTAY Platinum">WESTAY Platinum</SelectItem>
                          <SelectItem value="WESTAY Prive">WESTAY Prive</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="businessDescription"
                  render={({ field, fieldState }) => (
                    <FormItem>
                      <FormLabel>Description*</FormLabel>
                      <FormControl>
                        <Textarea
                          placeholder="Enter merchant description"
                          {...field}
                          className={`min-h-[100px] ${
                            fieldState.error ? "border-red-500" : ""
                          }`}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="websiteUrl"
                  render={({ field, fieldState }) => (
                    <FormItem>
                      <FormLabel>Website URL*</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="https://example.com"
                          className={fieldState.error ? "border-red-500" : ""}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                  <FormField
                    control={form.control}
                    name="country"
                    render={({ field }) => (
                      <FormItem className="w-full">
                        <FormLabel>Country*</FormLabel>
                        <Select
                          onValueChange={(value) => {
                            const selected = JSON.parse(value);
                            setSelectedCountry(selected);
                            field.onChange(value);
                            form.setValue("city", "");
                          }}
                          value={field.value}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select Country" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {(countries as any)?.map((country) => (
                              <SelectItem
                                key={country?.name}
                                value={JSON.stringify(country)}
                              >
                                {country?.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="city"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>City*</FormLabel>
                        <FormControl>
                          <VirtualizedCitySelect
                            cities={selectedCountry?.cities || []}
                            value={field.value}
                            onValueChange={field.onChange}
                            placeholder="Select City"
                            disabled={!selectedCountry?.cities?.length}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={form.control}
                  name="businessLocation"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Business Location</FormLabel>
                      <FormControl>
                        <Input placeholder="Enter location" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
            </CardContent>
          </Card>

          {/* Contact Information */}
          <Card className="border-0 shadow-sm overflow-hidden bg-white">
            <CardHeader className="card-header-compact bg-gray-50">
              <CardTitle className="text-lg font-medium text-gray-800">
                Contact Information
              </CardTitle>
              <p className="text-sm text-gray-600 mt-1">
                Add one or more contact persons for this merchant
              </p>
            </CardHeader>
            <CardContent className="p-6 space-y-6">
              <ContactForm form={form} />
              {form.formState.errors.contacts && (
                <p className="text-sm text-red-600">
                  {form.formState.errors.contacts.message}
                </p>
              )}
            </CardContent>
          </Card>

          <Card className="border-0 shadow-sm overflow-hidden bg-white">
            <CardHeader className="card-header-compact bg-gray-50">
              <CardTitle className="text-lg font-medium text-gray-800">
                Categories
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-6">
              {categoryFields.map((field, index) => (
                <div key={field.id} className="flex items-end gap-4">
                  <FormField
                    control={form.control}
                    name={`categoryVOList.${index}.categoryName`}
                    render={({ field }) => (
                      <FormItem className="flex-1">
                        <FormLabel>Category Name</FormLabel>
                        <FormControl>
                          <Input placeholder="Category Name" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name={`categoryVOList.${index}.cashbackPercentage`}
                    render={({ field }) => (
                      <FormItem className="flex-1">
                        <FormLabel>Cashback %</FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            placeholder="0"
                            min="0"
                            max="100"
                            step="0.01"
                            onWheel={(e) => e.currentTarget.blur()}
                            onKeyDown={(e) => {
                              // Prevent negative sign, 'e', 'E', '+'
                              if (["-", "e", "E", "+"].includes(e.key)) {
                                e.preventDefault();
                              }
                            }}
                            onChange={(e) => {
                              const value = e.target.value;
                              // Only allow numbers between 0-100
                              if (
                                value === "" ||
                                (parseFloat(value) >= 0 &&
                                  parseFloat(value) <= 100)
                              ) {
                                field.onChange(value);
                              }
                            }}
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="mb-1 text-red-600 hover:bg-red-100"
                    onClick={() => removeCategory(index)}
                    disabled={categoryFields.length === 1}
                    aria-label="Delete Category"
                  >
                    <Trash className="h-5 w-5" />
                  </Button>
                </div>
              ))}
              <Button
                type="button"
                variant="outline"
                onClick={() =>
                  appendCategory({
                    categoryCode: "",
                    categoryName: "",
                    cashbackPercentage: 0,
                  })
                }
              >
                Add Category
              </Button>
            </CardContent>
          </Card>

          <Separator className="my-8" />

          <div className="flex justify-end gap-3">
            <Button
              type="button"
              variant="outline"
              asChild
              className="border-rose-200 text-rose-900 hover:bg-rose-50"
            >
              <Link to={`${basePath}/merchants`}>Cancel</Link>
            </Button>
            <Button
              type="submit"
              disabled={isLoading}
              onClick={handleSubmitClick}
              className="bg-rose-600 text-white hover:bg-rose-700 focus-visible:ring-rose-500"
            >
              {isLoading ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              {isLoading ? "Creating..." : "Create Merchant"}
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
};

export default OffersCreateMerchant;
